import { useCallback, useState } from "react";

import { ConfigurationAttributeResult } from "@plansoft-configuration-renderer/configuration-api";

interface AccordionExpansionResult {
  expanded: boolean;
  handleExpansion: () => void;
}

interface AccordionExpansionOptions {
  isComplete: boolean;
  isSubstructure: boolean;
  selectedValueId?: string | number;
  displayedValueAttribute?: ConfigurationAttributeResult | null;
}

export function useAccordionExpansion({
  isComplete,
  isSubstructure,
  selectedValueId,
  displayedValueAttribute,
}: AccordionExpansionOptions): AccordionExpansionResult {
  const computeExpanded = (): boolean => {
    if (isSubstructure) return !isComplete;
    return !((selectedValueId || selectedValueId === 0) && isComplete);
  };

  const [expanded, setExpanded] = useState(computeExpanded);

  // Regular attribute: expand/collapse based on selection + completion
  // Substructure attribute: expand/collapse based on completion
  const [prevCompletionDeps, setPrevCompletionDeps] = useState({
    isSubstructure,
    selectedValueId,
    isComplete,
  });
  if (
    prevCompletionDeps.isSubstructure !== isSubstructure ||
    prevCompletionDeps.selectedValueId !== selectedValueId ||
    prevCompletionDeps.isComplete !== isComplete
  ) {
    setPrevCompletionDeps({ isSubstructure, selectedValueId, isComplete });
    setExpanded(computeExpanded());
  }

  // Substructure: collapse when displayed value changes
  const currentValue = displayedValueAttribute?.values?.find(
    (v) => v.isSelected || v.isEditable,
  )?.value;
  const [previousValue, setPreviousValue] = useState<unknown>(
    isSubstructure ? currentValue : undefined,
  );
  if (isSubstructure && previousValue !== currentValue) {
    if (previousValue !== undefined) {
      setExpanded(false);
    }
    setPreviousValue(currentValue);
  }

  const handleExpansion = useCallback((): void => {
    setExpanded((prev) => !prev);
  }, []);

  return { expanded, handleExpansion };
}
